import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, FileSpreadsheet, Image, FlaskConical, Dna, FileIcon,
  Presentation, BookOpen, Microscope,
  List, LayoutGrid, Table2, Search, ChevronDown,
  MoreVertical, Trash2, ExternalLink,
} from 'lucide-react';
import { documentsApi, GlobalDocument } from '../api/documents';
import { chemistryApi } from '../api/chemistry';
import { ChemFile } from '../types/chemistry';
import { useUIStore } from '../stores/uiStore';
import { formatRelativeDate } from '../utils/dateFormat';
import apiClient from '../api/client';
import FileDataCounts from '../components/common/FileDataCounts';
import { DATA_POINT_DESCRIPTION, formatDataCount, withFileDataSummary } from '../utils/fileDataSummary';

// ---------------------------------------------------------------------------
// Constants (shared with ProjectFilesTab)
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  chemistry: '#0D9488',
  biology: '#2563EB',
  literature: '#6B7280',
  presentation: '#8B5CF6',
  report: '#4B5563',
  image: '#EA580C',
  structure: '#2563EB',
  data: '#F59E0B',
  other: '#9CA3AF',
};

const CATEGORY_LABELS: Record<string, string> = {
  chemistry: 'Chemistry',
  biology: 'Biology',
  literature: 'Literature',
  presentation: 'Presentations',
  report: 'Reports',
  image: 'Images',
  structure: 'Structures',
  data: 'Data',
};

type ViewMode = 'list' | 'grid' | 'table';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function categoryIcon(category: string, size = 16) {
  const props = { size, strokeWidth: 1.5 };
  switch (category) {
    case 'chemistry': return <FlaskConical {...props} />;
    case 'biology': return <Dna {...props} />;
    case 'literature': return <BookOpen {...props} />;
    case 'presentation': return <Presentation {...props} />;
    case 'report': return <FileText {...props} />;
    case 'image': return <Image {...props} />;
    case 'structure': return <Microscope {...props} />;
    case 'data': return <FileSpreadsheet {...props} />;
    default: return <FileIcon {...props} />;
  }
}


// Read tracking
const READ_KEY = 'discoverant_files_read_global';
const LAST_SEEN_KEY = 'discoverant_files_seen_global';

function getReadFiles(): Record<number, number> {
  try {
    return JSON.parse(localStorage.getItem(READ_KEY) || '{}');
  } catch { return {}; }
}

function markFileRead(docId: number) {
  const read = getReadFiles();
  read[docId] = Date.now();
  localStorage.setItem(READ_KEY, JSON.stringify(read));
}

function getLastSeen(): number {
  try {
    return parseInt(localStorage.getItem(LAST_SEEN_KEY) || '0', 10);
  } catch { return 0; }
}

function setLastSeen() {
  localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
}

function isUnread(doc: GlobalDocument, lastSeen: number, readFiles: Record<number, number>): boolean {
  const docTime = new Date(doc.created_at).getTime();
  if (docTime <= lastSeen) return false;
  if (readFiles[doc.id]) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GlobalFilesPage: React.FC = () => {
  const navigate = useNavigate();
  const showNotification = useUIStore((s) => s.showNotification);

  const [allDocs, setAllDocs] = useState<GlobalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [countSort, setCountSort] = useState<'updated' | 'compoundCount' | 'biologicalDataPointCount'>('updated');
  const [readFiles, setReadFiles] = useState<Record<number, number>>(getReadFiles);
  const [lastSeen] = useState(getLastSeen);

  // Delete state
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<(GlobalDocument & { _chemFileId?: string }) | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Record visit
  useEffect(() => {
    const timer = setTimeout(() => setLastSeen(), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch — merge documents + chemistry files
  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both document system and chemistry files in parallel
      const [docData, chemFiles, projects] = await Promise.all([
        documentsApi.listAllDocuments({}).catch(() => [] as GlobalDocument[]),
        chemistryApi.getFiles().catch(() => [] as ChemFile[]),
        chemistryApi.getProjects().catch(() => [] as { id: number; name: string }[]),
      ]);

      // Build project name lookup
      const projectNames = new Map<number, string>();
      for (const p of projects) projectNames.set(p.id, p.name);

      // Track document IDs to avoid duplicates
      const docFileIds = new Set(docData.map(d => `${d.project_id}:${d.original_filename}`));

      // Convert chemistry files to GlobalDocument shape
      const chemDocs: GlobalDocument[] = chemFiles
        .filter(f => !docFileIds.has(`${f.projectId}:${f.originalFileName}`))
        .map(f => {
          const category = f.fileType === 'reactions' ? 'chemistry'
            : f.fileType === 'biologics' ? 'biology'
            : 'chemistry';
          return {
            id: -parseInt(f.id, 10), // Negative ID to distinguish from documents
            tenant_id: 0,
            project_id: f.projectId || 0,
            uploaded_by: '',
            original_filename: f.originalFileName || f.fileName,
            display_name: f.originalFileName || f.fileName,
            description: null,
            mime_type: null,
            category,
            tags: [],
            current_version: 1,
            processing_status: ['completed', 'ready'].includes(f.status) ? 'COMPLETE' : f.status?.toUpperCase() || 'COMPLETE',
            sharing_policy: 'private',
            is_archived: false,
            created_at: f.createdDate ? new Date(f.createdDate).toISOString() : new Date().toISOString(),
            updated_at: f.modifiedDate ? new Date(f.modifiedDate).toISOString() : (f.createdDate ? new Date(f.createdDate).toISOString() : new Date().toISOString()),
            project_name: projectNames.get(f.projectId || 0) || 'Unknown Project',
            _chemFileId: f.id, // Stash original ID for navigation
            compoundCount: f.compoundCount,
            biologicalDataPointCount: f.biologicalDataPointCount,
            biologicalEntityCount: f.biologicalEntityCount,
            dataSummaryStatus: f.dataSummaryStatus,
          } as GlobalDocument & { _chemFileId?: string };
        });

      setAllDocs([...docData.map(d => withFileDataSummary(d, chemFiles)), ...chemDocs]);
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Close menu on outside click
  useEffect(() => {
    if (menuOpenId === null) return;
    const handler = () => setMenuOpenId(null);
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [menuOpenId]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const chemFileId = (deleteTarget as any)._chemFileId;
      if (chemFileId) {
        await apiClient.delete(`/files/${chemFileId}`);
      } else {
        await apiClient.delete(`/v1/documents/${deleteTarget.id}`);
      }
      setAllDocs(prev => prev.filter(d => d.id !== deleteTarget.id));
      showNotification('success', 'File deleted successfully');
      setDeleteTarget(null);
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, showNotification]);

  // Unique projects for filter dropdown
  const projects = useMemo(() => {
    const map = new Map<number, string>();
    for (const d of allDocs) {
      if (!map.has(d.project_id)) map.set(d.project_id, d.project_name);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allDocs]);

  // Unique categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const d of allDocs) set.add(d.category);
    return Array.from(set).sort();
  }, [allDocs]);

  // Filter + sort newest first
  const filtered = useMemo(() => {
    let docs = allDocs;
    if (categoryFilter) docs = docs.filter(d => d.category === categoryFilter);
    if (projectFilter) docs = docs.filter(d => d.project_id === Number(projectFilter));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      docs = docs.filter(d =>
        d.display_name.toLowerCase().includes(q) ||
        d.original_filename.toLowerCase().includes(q)
      );
    }
    return [...docs].sort((a, b) => countSort === 'updated'
      ? new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      : (b[countSort] ?? -1) - (a[countSort] ?? -1));
  }, [allDocs, categoryFilter, projectFilter, searchQuery, countSort]);

  // Split new/earlier
  const { newDocs, earlierDocs, newCount } = useMemo(() => {
    const nd: GlobalDocument[] = [];
    const ed: GlobalDocument[] = [];
    for (const d of filtered) {
      if (isUnread(d, lastSeen, readFiles)) nd.push(d);
      else ed.push(d);
    }
    return { newDocs: nd, earlierDocs: ed, newCount: nd.length };
  }, [filtered, lastSeen, readFiles]);

  const handleDocClick = useCallback((doc: GlobalDocument) => {
    markFileRead(doc.id);
    setReadFiles({ ...getReadFiles() });
    // Chemistry files (negative ID): navigate directly to editor
    const chemFileId = (doc as any)._chemFileId;
    if (chemFileId) {
      navigate(`/chemistry/file/${chemFileId}/editor`);
    } else {
      // Document system files: navigate to project files tab
      navigate(`/chemistry/files?project=${doc.project_id}`);
    }
  }, [navigate]);

  const handleProjectClick = useCallback((e: React.MouseEvent, projectId: number) => {
    e.stopPropagation();
    navigate(`/chemistry/files?project=${projectId}`);
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderRowMenu = (doc: GlobalDocument) => {
    const isOpen = menuOpenId === doc.id;
    return (
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpenId(isOpen ? null : doc.id); }}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Actions"
        >
          <MoreVertical size={14} style={{ color: 'var(--text-muted, #9ca3af)' }} />
        </button>
        {isOpen && (
          <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50">
            <button
              onClick={() => { handleDocClick(doc); setMenuOpenId(null); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <ExternalLink size={13} /> Open
            </button>
            <div className="h-px bg-gray-100 dark:bg-gray-700 my-0.5" />
            <button
              onClick={() => { setDeleteTarget(doc as any); setMenuOpenId(null); }}
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderFileRow = (doc: GlobalDocument, unread: boolean) => (
    <div
      key={doc.id}
      onClick={() => handleDocClick(doc)}
      className="px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b"
      style={{
        display: 'grid',
        gridTemplateColumns: '20px minmax(200px, 4fr) minmax(100px, 2fr) 100px 50px 80px 32px',
        gap: '10px',
        alignItems: 'center',
        borderColor: 'var(--card-border, #e5e7eb)',
        minHeight: 48,
        borderLeft: unread ? '3px solid #0D9488' : '3px solid transparent',
      }}
    >
      {/* File icon */}
      <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF', position: 'relative' }}>
        {unread && (
          <span className="absolute -left-1.5 -top-0.5 w-1.5 h-1.5 rounded-full bg-teal-500" />
        )}
        {categoryIcon(doc.category, 16)}
      </span>

      {/* Filename */}
      <span
        className="text-sm min-w-0"
        style={{
          color: 'var(--text-primary, #111827)',
          fontWeight: unread ? 600 : 400,
        }}
      >
        <span className="block truncate">{doc.display_name}</span>
        <FileDataCounts data={doc} />
      </span>

      {/* Project name */}
      <button
        onClick={(e) => handleProjectClick(e, doc.project_id)}
        className="text-xs px-2 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors truncate text-left"
        style={{ color: 'var(--text-secondary, #6b7280)' }}
        title={doc.project_name}
      >
        {doc.project_name}
      </button>

      {/* Category pill */}
      <span>
        <span
          style={{
            display: 'inline-block',
            padding: '1px 8px',
            borderRadius: 9999,
            fontSize: 10,
            fontWeight: 600,
            color: '#fff',
            backgroundColor: CATEGORY_COLORS[doc.category] || '#9CA3AF',
            textTransform: 'capitalize',
          }}
        >
          {doc.category}
        </span>
      </span>

      {/* Version */}
      <span
        style={{ textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}
      >
        v{doc.current_version}
      </span>

      {/* Date */}
      <span
        style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}
      >
        {formatRelativeDate(doc.updated_at)}
      </span>

      {/* Actions menu */}
      {renderRowMenu(doc)}
    </div>
  );

  const renderListView = () => (
    <div className="flex-1 overflow-y-auto">
      {/* New files section */}
      {countSort === 'updated' && newDocs.length > 0 && (
        <>
          <div className="px-3 py-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-teal-50/50 dark:bg-teal-900/10 border-b border-teal-100 dark:border-teal-900/20">
            {newDocs.length} new file{newDocs.length !== 1 ? 's' : ''}
          </div>
          {newDocs.map(doc => renderFileRow(doc, true))}
          {earlierDocs.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
              Earlier
            </div>
          )}
        </>
      )}
      {(countSort === 'updated' ? earlierDocs : filtered).map(doc => renderFileRow(doc, isUnread(doc, lastSeen, readFiles)))}
      {filtered.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-14 h-14 mb-3 bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 rounded-full flex items-center justify-center">
            <FileText className="w-7 h-7 text-emerald-400" />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary, #6b7280)' }}>No files found</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted, #9ca3af)' }}>
            {searchQuery ? 'Try a different search term' : 'Upload files to a project to see them here'}
          </p>
        </div>
      )}
    </div>
  );

  const renderGridView = () => (
    <div
      className="flex-1 overflow-y-auto p-2"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 12,
        alignContent: 'start',
      }}
    >
      {filtered.map(doc => {
        const unread = isUnread(doc, lastSeen, readFiles);
        return (
          <div
            key={doc.id}
            onClick={() => handleDocClick(doc)}
            className="flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 relative"
            style={{
              backgroundColor: 'var(--card-bg, #fff)',
              borderColor: unread ? '#0D9488' : 'var(--card-border, #e5e7eb)',
              borderWidth: unread ? 2 : 1,
              borderRadius: 8,
            }}
          >
            {/* Actions menu */}
            <div className="absolute top-2 right-2">
              {renderRowMenu(doc)}
            </div>

            {/* Large icon */}
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center mb-3 relative"
              style={{ backgroundColor: `${CATEGORY_COLORS[doc.category] || '#9CA3AF'}15` }}
            >
              <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF' }}>
                {categoryIcon(doc.category, 24)}
              </span>
              {unread && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-white dark:border-gray-900" />
              )}
            </div>

            {/* Filename */}
            <span
              className="text-sm text-center truncate w-full"
              style={{
                color: 'var(--text-primary, #111827)',
                fontWeight: unread ? 600 : 500,
              }}
              title={doc.display_name}
            >
              {doc.display_name}
            </span>

            {/* Project name */}
            <button
              onClick={(e) => handleProjectClick(e, doc.project_id)}
              className="text-[10px] mt-1 hover:text-teal-600 transition-colors truncate max-w-full"
              style={{ color: 'var(--text-muted, #9ca3af)' }}
              title={doc.project_name}
            >
              {doc.project_name}
            </button>

            <div className="mt-2 text-center"><FileDataCounts data={doc} /></div>

            {/* Category + version row */}
            <div className="flex items-center gap-2 mt-2">
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 6px',
                  borderRadius: 9999,
                  fontSize: 9,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: CATEGORY_COLORS[doc.category] || '#9CA3AF',
                  textTransform: 'capitalize',
                }}
              >
                {doc.category}
              </span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--text-muted, #9ca3af)' }}>
                v{doc.current_version}
              </span>
            </div>

            {/* Date */}
            <span className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted, #9ca3af)' }}>
              {formatRelativeDate(doc.updated_at)}
            </span>
          </div>
        );
      })}
      {filtered.length === 0 && !loading && (
        <div className="col-span-full flex flex-col items-center justify-center py-16">
          <div className="w-14 h-14 mb-3 bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 rounded-full flex items-center justify-center">
            <FileText className="w-7 h-7 text-emerald-400" />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary, #6b7280)' }}>No files found</p>
        </div>
      )}
    </div>
  );

  const renderTableView = () => (
    <div className="flex-1 overflow-y-auto">
      {/* Table header */}
      <div
        className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider border-b sticky top-0 z-10"
        style={{
          display: 'grid',
          gridTemplateColumns: '20px minmax(180px, 4fr) minmax(90px, 2fr) 85px 105px 90px 40px 60px 80px 32px',
          gap: '12px',
          alignItems: 'center',
          color: 'var(--text-muted, #9ca3af)',
          backgroundColor: 'var(--surface-bg, #f9fafb)',
          borderColor: 'var(--card-border, #e5e7eb)',
        }}
      >
        <span />
        <span>Name</span>
        <span>Project</span>
        <button className="text-right" onClick={() => setCountSort('compoundCount')}>Compounds</button>
        <button className="text-right" title={DATA_POINT_DESCRIPTION} onClick={() => setCountSort('biologicalDataPointCount')}>Bio data points</button>
        <span>Category</span>
        <span style={{ textAlign: 'center' }}>Ver</span>
        <span>Status</span>
        <span>Modified</span>
        <span />
      </div>
      {filtered.map(doc => {
        const unread = isUnread(doc, lastSeen, readFiles);
        return (
          <div
            key={doc.id}
            onClick={() => handleDocClick(doc)}
            className="px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b"
            style={{
              display: 'grid',
              gridTemplateColumns: '20px minmax(180px, 4fr) minmax(90px, 2fr) 85px 105px 90px 40px 60px 80px 32px',
              gap: '12px',
              alignItems: 'center',
              borderColor: 'var(--card-border, #e5e7eb)',
              height: 40,
              borderLeft: unread ? '3px solid #0D9488' : '3px solid transparent',
            }}
          >
            {/* File icon */}
            <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF' }}>
              {unread && <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-500 mr-1" />}
              {categoryIcon(doc.category, 14)}
            </span>

            {/* Name */}
            <span
              className="text-sm truncate"
              style={{ color: 'var(--text-primary, #111827)', fontWeight: unread ? 600 : 400 }}
            >
              {doc.display_name}
            </span>

            {/* Project */}
            <button
              onClick={(e) => handleProjectClick(e, doc.project_id)}
              className="text-xs truncate hover:text-teal-600 transition-colors text-left"
              style={{ color: 'var(--text-secondary, #6b7280)' }}
              title={doc.project_name}
            >
              {doc.project_name}
            </button>

            <span className="text-right text-xs tabular-nums">{formatDataCount(doc.compoundCount)}</span>
            <span className="text-right text-xs tabular-nums" title={DATA_POINT_DESCRIPTION}>{formatDataCount(doc.biologicalDataPointCount)}</span>

            {/* Category pill */}
            <span>
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 6px',
                  borderRadius: 9999,
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: CATEGORY_COLORS[doc.category] || '#9CA3AF',
                  textTransform: 'capitalize',
                }}
              >
                {doc.category}
              </span>
            </span>

            {/* Version */}
            <span
              style={{ textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}
            >
              v{doc.current_version}
            </span>

            {/* Status */}
            <span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: doc.processing_status === 'COMPLETE' ? '#10B981' : doc.processing_status === 'ERROR' ? '#EF4444' : '#F59E0B',
                }}
              >
                {doc.processing_status === 'COMPLETE' ? 'Complete' : doc.processing_status}
              </span>
            </span>

            {/* Modified */}
            <span style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}>
              {formatRelativeDate(doc.updated_at)}
            </span>

            {/* Actions menu */}
            {renderRowMenu(doc)}
          </div>
        );
      })}
      {filtered.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-14 h-14 mb-3 bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 rounded-full flex items-center justify-center">
            <FileText className="w-7 h-7 text-emerald-400" />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary, #6b7280)' }}>No files found</p>
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--page-bg, #fff)' }}>
      {/* Page header */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--card-border, #e5e7eb)' }}>
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #111827)' }}>Files</h1>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted, #9ca3af)' }}>
          All files across your projects
        </p>
      </div>

      {/* Filter bar */}
      <div className="px-4 py-2.5 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: 'var(--card-border, #e5e7eb)' }}>
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            style={{ borderColor: 'var(--card-border, #d1d5db)' }}
          />
        </div>

        <select aria-label="Sort files" value={countSort}
          onChange={e => setCountSort(e.target.value as typeof countSort)}
          className="px-3 py-1.5 text-xs border rounded-md bg-white dark:bg-gray-900">
          <option value="updated">Recently modified</option>
          <option value="compoundCount">Most compounds</option>
          <option value="biologicalDataPointCount">Most bio data points</option>
        </select>

        {/* Category filter */}
        <div className="relative">
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="appearance-none pl-3 pr-7 py-1.5 text-xs border rounded-md bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
            style={{ borderColor: 'var(--card-border, #d1d5db)', color: 'var(--text-primary, #111827)' }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
        </div>

        {/* Project filter */}
        <div className="relative">
          <select
            value={projectFilter}
            onChange={e => setProjectFilter(e.target.value)}
            className="appearance-none pl-3 pr-7 py-1.5 text-xs border rounded-md bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
            style={{ borderColor: 'var(--card-border, #d1d5db)', color: 'var(--text-primary, #111827)' }}
          >
            <option value="">All Projects</option>
            {projects.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
        </div>

        {/* File count */}
        <span className="text-xs ml-auto" style={{ color: 'var(--text-muted, #9ca3af)' }}>
          {filtered.length} file{filtered.length !== 1 ? 's' : ''}
          {newCount > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 text-teal-600 dark:text-teal-400 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
              {newCount} new
            </span>
          )}
        </span>

        {/* View mode toggle */}
        <div className="flex items-center gap-0.5 border rounded-md p-0.5" style={{ borderColor: 'var(--card-border, #d1d5db)' }}>
          {([
            { mode: 'list' as ViewMode, icon: List, label: 'List' },
            { mode: 'grid' as ViewMode, icon: LayoutGrid, label: 'Grid' },
            { mode: 'table' as ViewMode, icon: Table2, label: 'Table' },
          ]).map(({ mode, icon: Icon, label }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              title={label}
              className={`p-1 rounded transition-colors ${
                viewMode === mode
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
          <span className="text-sm" style={{ color: 'var(--text-muted, #9ca3af)' }}>Loading files...</span>
        </div>
      ) : (
        viewMode === 'list' ? renderListView() :
        viewMode === 'grid' ? renderGridView() :
        renderTableView()
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 100000, backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => { if (!deleting) setDeleteTarget(null); }}
        >
          <div
            className="rounded-xl shadow-2xl w-full max-w-md mx-4"
            style={{ backgroundColor: 'var(--card-bg, #fff)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Delete File</h3>
              <p className="text-sm mt-1.5" style={{ color: 'var(--text-secondary)' }}>
                Delete <strong>{deleteTarget.display_name}</strong>? This will remove the file and all associated molecule data. This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-lg transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-40"
                style={{ backgroundColor: '#dc2626' }}
                onMouseEnter={(e) => { if (!deleting) (e.target as HTMLElement).style.backgroundColor = '#b91c1c'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#dc2626'; }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalFilesPage;
